//=============================================
// ARCHELIOS MAP NAMESPACE
//=============================================
var am = (function(module) {

    //Use strict mode
    'use strict';

    /**
     * Class simulating a PV panel
     */
    module.PvPanelInstallation = function() {
        // Private attributes
        this._roofModel = undefined; // reference to the object computing the solar potential of the selected roof sides
        this._params = undefined;    // parameters
        this._properties = {};
        this._currRoofId = undefined; // ID of the currently selected roof
    };

    /**
     * Class constructor
     * @constructor
     *
     * @param {am.ModelBaseClass} roofModel - Reference to the SolarPotentialModel instance to be used in order to 
     *      compute the solar potential of the PV panel installation.
     * @param {Object} params - Object configuration. The following keys are required:
     *      - pvPanelYield: [0-1] Yield of the solar panels, i.e. fraction of the incoming solar energy which is indeed 
     *          converted in electrical energy
     *      - pvPanelPurchasePrice: [euros/m2] PV panel purchase price
     *      - electricitySalePrice: [euros/kWh] Price at which the user will sell the electricity that (s)he produces
     *      - electricityPurchasePrice: [euros/kWh] Price at which the user buys the electricity that (s)he consumes
     *      - electricityConsumptionPerInhabitant: [kWh/pers.]
     */
    module.PvPanelInstallation.prototype.init = function(roofModel, params) {
        this._roofModel = roofModel;
        this._params = params;
    };

    /**
     * Class destructor
     */
    module.PvPanelInstallation.prototype.destroy = function() {
        this._roofModel = undefined;
        this._params = undefined;
    };

    /**
     * Computes the characteristics of the PV panel installation
     *
     * @param {Object} userInfos - Information about the (future) owner of the PV installation. This object must 
     *      contain the following keys:
     *          - userCategory: [individual|professionnal|administration] User category
     *          - annualElectricityConsumption: [kWh] Annual electricity consumption
     *          - annualElectricityBill: [euros] Annual electricity bill
     *          - hasElectricalHeating: [true|false] Is the building equipped with electrical heating?
     *          - hasElectricalBoiler: [true|false] Is the building equipped with an electrical boiler?
     *          - numberOfInhabitants: [-] Number of inhabitants in the building
     *          - numberOfFloors: [-] Number of floors in the building
     *          - buildingGroundArea: [m2] Surface area of the building's ground floor
     */
    module.PvPanelInstallation.prototype.computeProperties = function(userInfos) {
        var optimalSurfaceForSale = this._properties.optimalSurfaceForElectricitySale;
        var optimalSurfaceForAuto = this._properties.optimalSurfaceForAutoconsumption;

        this._roofModel.computeAnnualIrradiation(userInfos, "pv");
        var meanIrrad = this._roofModel.getProperty("meanSpecificAnnualSolarEnergy");
        this._properties = this._computeAllProperties(userInfos, userInfos.panelSurface, meanIrrad);

        // Compute the optimal surface
        if (this._currRoofId== undefined || this._currRoofId !== this._roofModel.getProperty("roofId")) {
            this._currRoofId = this._roofModel.getProperty("roofId");


            var minAvailableSurface = this._roofModel.getProperty("minAvailablePanelSurface").pv;
            var maxAvailableSurface = this._roofModel.getProperty("maxAvailablePanelSurface").pv;
            
            //#135 on initialise la surface au max ( pour les cas jamais rentable)
            optimalSurfaceForSale = maxAvailableSurface;
            optimalSurfaceForAuto = maxAvailableSurface;

            //#135 on initialise le ROI a infinity
            var bestROIForSale =Infinity; //this._properties.economicsResults['sale'].returnOnInvestment;
            var bestROIForAuto =Infinity;// this._properties.economicsResults['bestAuto'].returnOnInvestment;

            for (var surface = minAvailableSurface; surface <= maxAvailableSurface; ++surface) {
                this._roofModel.computeAnnualIrradiation({panelSurface: surface}, "pv");
                meanIrrad = this._roofModel.getProperty("meanSpecificAnnualSolarEnergy");
                var properties = this._computeAllProperties(userInfos, surface, meanIrrad);
                var saleROI = properties.economicsResults['sale'].returnOnInvestment;
                if (saleROI < bestROIForSale) {
                    bestROIForSale = saleROI;
                    optimalSurfaceForSale = surface;
                }
                var autoROI = properties.economicsResults['bestAuto'].returnOnInvestment;
                if (autoROI < bestROIForAuto) {
                    bestROIForAuto = autoROI;
                    optimalSurfaceForAuto = surface;
                }
            }
        }

        this._properties.optimalSurfaceForElectricitySale   = optimalSurfaceForSale;
        this._properties.optimalPeakPowerForElectricitySale = optimalSurfaceForSale * this._params.pvPanelYield;
        this._properties.optimalSurfaceForAutoconsumption   = optimalSurfaceForAuto;
        this._properties.optimalPeakPowerForAutoconsumption = optimalSurfaceForAuto * this._params.pvPanelYield;
    };

    module.PvPanelInstallation.prototype._computeAllProperties = function(userInfos, panelSurface, meanIrrad) {
        //=== PRODUCTION ==
        // Compute the annual electricity production (and its equivalent in number of persons whose electricity needs
        // are covered by the production)
        var peakPower = this._params.pvPanelYield*panelSurface * 1000;
        var elecProduction = peakPower*this._params.pvPerformanceRatio * meanIrrad / 1000;
        var prodProfile = this._getProdProfile(elecProduction);
        var annualCO2Savings = this._params.hasOwnProperty('geqCO2savedPerKWh') ? 1e-3 * elecProduction * this._params.geqCO2savedPerKWh : undefined;

        //== CONSO ==
        var buildingSurfaceArea = this._roofModel.getProperty("buildingGroundArea");

        // Estimate the annual electricity consumption
        var baseElecConsumption = 0.;    // [kWh]
        var heatingElecConsumption = 0.; // [kWh]
        var totalElecConsumption = 0.;   // [kWh]
        if (userInfos.hasOwnProperty("elecConsumptionInfo") && userInfos.elecConsumptionInfo === "hourlyCurve" &&
            this._params.profiles.individual.hasOwnProperty("userProfile")) {
            baseElecConsumption = this._params.profiles.individual.userProfile.somme;
            totalElecConsumption = this._params.profiles.individual.userProfile.somme;
        } else if (userInfos.hasOwnProperty("elecConsumptionInfo") && userInfos.elecConsumptionInfo === "annualSum") {
            baseElecConsumption = userInfos.annualElectricityConsumption;
            totalElecConsumption = userInfos.annualElectricityConsumption;
        } else {
            if (userInfos.hasElectricalHeating) {
                heatingElecConsumption += 100/*[kWh/m2]*/ * buildingSurfaceArea * userInfos.numberOfFloors;
            }
            if (userInfos.hasElectricalBoiler) {
                baseElecConsumption += 700/*[kWh/pers.]*/ * userInfos.numberOfInhabitants;
            }
            baseElecConsumption += 2750; // [kWh] Specific electricity consumption according to ADEME
            totalElecConsumption = baseElecConsumption + heatingElecConsumption;
        }

        // Compute the rate of autoconsumption
        var autoConsoElec = 0.;
        if (userInfos.userCategory === "individual") {
            var comsumptionProfile;
            if (userInfos.hasOwnProperty("elecConsumptionInfo") && userInfos.elecConsumptionInfo === "hourlyCurve" &&
                this._params.profiles.individual.hasOwnProperty("userProfile")) {
                comsumptionProfile = this._params.profiles.individual.userProfile.values;
            } else {
                comsumptionProfile = this._getConsoProfile(userInfos, baseElecConsumption, heatingElecConsumption);//#2311
            }
            autoConsoElec = this._computeAutoConso(prodProfile, comsumptionProfile);//#2311
        }
        var autoconsumptionRate = autoConsoElec/elecProduction;
        var autoproductionRate = autoConsoElec/totalElecConsumption;

        // Compute the loan parameters (percentage of the amount borrowed, own funds)
        var loanParameters = {
            borrowedElecSaleAmountPercentage: this._params.economics_pv.creditInitialAmountPercentage / 100 || 0,
            borrowedAutoConsoAmountPercentage: this._params.economics_pv.creditInitialAmountPercentage / 100 || 0
        };
        if ((userInfos.pvElecSaleBorrowedPercentage >= 0) && (userInfos.pvElecSaleBorrowedPercentage <= 100)) {
            loanParameters.borrowedElecSaleAmountPercentage = userInfos.pvElecSaleBorrowedPercentage / 100;//#2311
        }
        if ((userInfos.pvAutoconsoBorrowedPercentage >= 0) && (userInfos.pvAutoconsoBorrowedPercentage <= 100)) {
            loanParameters.borrowedAutoConsoAmountPercentage = userInfos.pvAutoconsoBorrowedPercentage / 100;//#2311
        }
        if (userInfos.hasOwnProperty('creditPersonalFunds')) {
            loanParameters.ownFunds = userInfos.creditPersonalFunds;//#2311
        }

        return {
            installPeakPower: peakPower,
            annualElectricityConsumption: totalElecConsumption,
            annualElectricityProduction: elecProduction,
            pvAnnualCO2Savings: Math.round(annualCO2Savings),
            electricityProductionEquivalent: Math.round(elecProduction / this._params.electricityConsumptionPerInhabitant),
            annualSavingProduction: autoConsoElec,
            electricityAutoconsumptionRate: autoconsumptionRate,
            electricityAutoproductionRate: autoproductionRate,
            electricityAutoproductionEquivalent: Math.round(autoConsoElec / this._params.electricityConsumptionPerInhabitant),
            economicsResults: this._computeEco(userInfos, peakPower, elecProduction, totalElecConsumption, autoConsoElec, loanParameters)
        };
    };

    /**
     * Calcule des trois resultats eco
     * 
     * #2311
     * @param {Object} userInfos -
     * ajout dans _properties.economicsResults.sale, auto, autoAndSale 
     *  - observationPeriod [year]
     *  - pvPanelInstallationCost [€]
     *  - totalSubventionOverPeriod [€]
     *  - fNetProfitOverPeriod [€]
     *  - electricitySaleIncomeOverPeriod [€]
     *  - electricityBillSavingsOverPeriod [€]
     *  - electricityConnectionCostOverPeriod [€]
     *  - electricitySaleIncomeOn1stYear [€]
     *  - electricityBillSavingsOn1stYear [€]
     *  - electricityConnectionCostOn1stYear [€] 
     */
    module.PvPanelInstallation.prototype._computeEco=function(userInfos, peakPower, annualElecProduction, annualElecConsumption, annualSavingProduction, loanParameters){
        this._sUserCategory = userInfos.userCategory;
        var simulateBankLoan = userInfos.hasOwnProperty('simulateBankLoan') ? userInfos.simulateBankLoan : true;

        var economicsResults = {};
        
        var sComputeTypeA=["sale","auto","autoAndSale"];
        var oBestResAuto=null;
        for(var iNumCompute=0;iNumCompute<sComputeTypeA.length;iNumCompute++){
            var sType=sComputeTypeA[iNumCompute];
            var oRes;

            oRes = this._computeEcoFromMode(sType, peakPower, annualElecProduction, annualElecConsumption, annualSavingProduction, simulateBankLoan, loanParameters);
            economicsResults[sType]=oRes;

            if(sType!="sale" &&(oBestResAuto==null||oBestResAuto.returnOnInvestment>oRes.returnOnInvestment)){
                oBestResAuto=oRes;
            }
        }
        if(oBestResAuto!=null)
            economicsResults["bestAuto"]=oBestResAuto;

        return economicsResults;
    };


    /**
     * Calcul economique en function du mode en cours
     * 
     * #2311
     * 
     * @param {String} sMode mode de calcul : sale/auto/autoAndSale 
     * 
     * @return {Object} :
     *  - observationPeriod [year]
     *  - pvPanelInstallationCost [€]
     *  - totalSubventionOverPeriod [€]
     *  - fNetProfitOverPeriod [€]
     *  - electricitySaleIncomeOverPeriod [€]
     *  - electricityBillSavingsOverPeriod [€]
     *  - electricityConnectionCostOverPeriod [€]
     *  - electricitySaleIncomeOn1stYear [€]
     *  - electricityBillSavingsOn1stYear [€]
     *  - electricityConnectionCostOn1stYear [€]
     */
    module.PvPanelInstallation.prototype._computeEcoFromMode =function(sMode, peakPower, annualElecProduction, annualElecConsumption, annualSavingProduction, simulateBankLoan, loanParameters){
        var minComputablePeakPower = 1000 /* Wc */;
        var maxComputablePeakPower = 100000 /* Wc */;

        if (peakPower >= minComputablePeakPower && peakPower <= maxComputablePeakPower) {
            this._sMode=sMode;
            var self = this;
            var fPeakPower = peakPower/1000;//[kWc]
            var getEcoParam = function(sParamName) {
                return self._getEcoParam(sParamName, fPeakPower);
            };
            var fSaleElecPriceInflation = getEcoParam("saleElecPriceInflation");
            var fBuyElecPriceInflation  = getEcoParam("buyElecPriceInflation");
            var fInflation              = getEcoParam("inflation");
            var fActualisationRate      = getEcoParam("actualisationRate");

            // ======================
            // Estimation de l'investissement brut
            // ======================

            // Cout du Wc
            var fModulePriceHT = 0.;
            if (this._params["economics_pv"].hasOwnProperty("installationCostPerKWc")) { // new method for computing the installation price: piecewise linear interpolation between known prices
                fModulePriceHT = getEcoParam("installationCostPerKWc");
            } else { // old method for computing the installation price: power interpolation of the piecewise linear function
                fModulePriceHT = getEcoParam("installationCostRegressionFactor") * Math.pow(fPeakPower, getEcoParam("installationCostRegressionPower"));
            }

            // Cout de l'install
            var tvaFactor = 1 + getEcoParam("tvaRate");
            var fRawInstallationCostWoConnectionHT  = Math.round((fPeakPower * 1000. * fModulePriceHT) / 100.) * 100.; //[€ HT]
            var fRawInstallationCostWoConnectionTTC = fRawInstallationCostWoConnectionHT * tvaFactor;

            // Calcul du prix de raccordement
            var fRawConnectionPriceHT  = getEcoParam("connectionCost") + getEcoParam("S3RENR_coef") * fPeakPower;//[€ HT]
            var fNetConnectionPriceHT  = (1 - getEcoParam("refactionRate")) * fRawConnectionPriceHT;//[€ HT]
            var fNetConnectionPriceTTC = fNetConnectionPriceHT * tvaFactor;//[€ TTC]

            // Calcul de l'investissement brut (i.e. sans subventions)
            var fRawInstallationCostWithConnectionHT  = fRawInstallationCostWoConnectionHT + fNetConnectionPriceHT; //[€ HT}
            var fRawInstallationCostWithConnectionTTC = fRawInstallationCostWithConnectionHT * tvaFactor; //[€ TTC]


            // ======================
            // Estimation de la subvention
            // ======================

            // Calcul de subventions supplementaires eventuelles
            var extraSubsidyTTC = 0;
            var extraSubsidyScheme = this._params.economics_pv.extraSubventionScheme;
            if (extraSubsidyScheme !== undefined && extraSubsidyScheme !== "") {

                if (extraSubsidyScheme === "Grand Est") {
                    var autoconsumptionRate = annualSavingProduction / annualElecProduction;
                    var autoproductionRate  = annualSavingProduction / annualElecConsumption;

                    var subPerKwc = 0.;
                    if (this._sUserCategory == "professional" && this._sMode != "sale" && autoconsumptionRate > 0.7) {
                        subPerKwc = (autoproductionRate < 0.15) ? 250. : 300.;
                    } else if (this._sUserCategory == "administration") {
                        subPerKwc = (this._sMode == "sale" || autoconsumptionRate < 0.7) ? 200. :
                            (autoproductionRate < 0.15) ? 250. : 300.;
                    }

                    extraSubsidyTTC = Math.min(0.25 * fRawInstallationCostWoConnectionHT, subPerKwc * fPeakPower);
                } else {
                    throw new Error("Unknown extra subsidy scheme '" + extraSubsidyScheme + "'");
                }
            }

            // Subvention de l'install
            var fSubventionDuration = getEcoParam("subventionYearDuration");//[year]
            var fSubventionOverPeriodTTC = getEcoParam("subventionRate") * fPeakPower * 1000 + extraSubsidyTTC; //[€ TTC]

            // Calcul de l'investissement net
            var fNetInstallationCostWithConnectionTTC = fRawInstallationCostWithConnectionTTC - fSubventionOverPeriodTTC;  //[€ TTC]


            // ======================
            // Calcul de la rentabilité
            // ======================

            // Durées d'observation et de vente
            var fSalePriceYearDuration   = getEcoParam("salePriceYearDuration"); //[year]
            var fObservationYearDuration = (this._sMode == "sale") ? fSalePriceYearDuration : getEcoParam("observationYearDuration"); //[year]

            // Prix d'achat du kWh (pour le calcul des économies)
            var fBuyElecPricePerKwhTTC = getEcoParam("buyElecPrice_HTVA") * (1 + getEcoParam("elecTvaRate"));//[€ TTC/kWh]

            // Calcul des revenus annuels
            var fAnnualSaleIncomesTTC = 0; //[€ TTC/year]
            var fAnnualSavingsTTC     = 0; //[€ TTC/year]
            switch (this._sMode) {
                case "sale":
                    fAnnualSaleIncomesTTC = annualElecProduction * getEcoParam("saleElecPrice");
                    break;
                case "auto":
                    fAnnualSavingsTTC = fBuyElecPricePerKwhTTC * annualSavingProduction;
                    break;
                case "autoAndSale":
                    fAnnualSavingsTTC = fBuyElecPricePerKwhTTC * annualSavingProduction;
                    fAnnualSaleIncomesTTC = Math.max(0, annualElecProduction - annualSavingProduction) * getEcoParam("saleElecPrice");
                    break;
                default :
                    console.warn("error bad computation mode : " + this._sMode);
            }

            // Charges d'exploitation annuelles
            var fDepEmTTC = 0.;
            if (this._params.economics_pv.hasOwnProperty("maintenanceCostRegressionFactor") && this._params.economics_pv.hasOwnProperty("maintenanceCostRegressionPower")) {
                var coeffDepEm = getEcoParam("maintenanceCostRegressionFactor") * Math.pow(fPeakPower, getEcoParam("maintenanceCostRegressionPower"));
                fDepEmTTC = Math.round(coeffDepEm * fRawInstallationCostWoConnectionHT / 10) * 10;
            } else {
                fDepEmTTC = getEcoParam("maintenanceCost");
            }
            var fAnnualOpexTTC = fDepEmTTC + getEcoParam("TURPE_fixe");

            // Calcul de flux
            var fIncomeOverPeriodTTC = this.getDiscountedValue(fAnnualSaleIncomesTTC, fActualisationRate, fSaleElecPriceInflation, fSalePriceYearDuration);
            var fBillSavingsOverPeriodTTC = this.getDiscountedValue(fAnnualSavingsTTC, fActualisationRate, fBuyElecPriceInflation, fObservationYearDuration);
            var fOpexOverPeriodTTC = this.getDiscountedValue(fAnnualOpexTTC, fActualisationRate, fInflation, fObservationYearDuration);


            // ======================
            // Calcul du crédit
            // ======================

            // Calcul du montant emprunté et de l'apport propre
            var borrowedAmountPercentage, fAmountBorrowedTTC, fOwnFundsTTC;
            if (simulateBankLoan) {
                borrowedAmountPercentage = (sMode === 'sale') ? loanParameters.borrowedElecSaleAmountPercentage : loanParameters.borrowedAutoConsoAmountPercentage;
                fAmountBorrowedTTC = fNetInstallationCostWithConnectionTTC * borrowedAmountPercentage;
                fOwnFundsTTC = fNetInstallationCostWithConnectionTTC - fAmountBorrowedTTC ;
                if (loanParameters.hasOwnProperty('ownFunds')) {
                    fOwnFundsTTC = Math.min(loanParameters.ownFunds, fNetInstallationCostWithConnectionTTC);
                    fAmountBorrowedTTC = fNetInstallationCostWithConnectionTTC - fOwnFundsTTC;
                    borrowedAmountPercentage = fAmountBorrowedTTC / fNetInstallationCostWithConnectionTTC;
                }
            } else {
                fOwnFundsTTC = fNetInstallationCostWithConnectionTTC;
                fAmountBorrowedTTC = 0;
                borrowedAmountPercentage = 0;
            }

            // Durée et intérêt de l'emprunt
            var fCreditYearlyRate = getEcoParam("creditYearlyRate");
            if (fCreditYearlyRate == undefined) {
                fCreditYearlyRate = 3; // 3% default value
            }
            var fCreditMonthlyRate = fCreditYearlyRate / 1200. ; // monthly rate
            var fCreditDurationInYears = getEcoParam("creditDurationInYears") || 20;
            if (fCreditDurationInYears == undefined) {
                fCreditDurationInYears = 20; // default to 20 years
            }
            var fCreditDurationInMonths = fCreditDurationInYears * 12;

            // Calcul des intérêts (montant total à rembourser à la banque + montant des intérêts)
            var _fCoef = Math.pow(1.+fCreditMonthlyRate, fCreditDurationInMonths) ;

            var _nMonthlyRepayment;
            if ( fCreditMonthlyRate > 1.e-7 ) {
                _nMonthlyRepayment = fAmountBorrowedTTC * fCreditMonthlyRate * _fCoef / (_fCoef-1.) ;
            } else {
                _nMonthlyRepayment = fAmountBorrowedTTC / fCreditDurationInMonths ;
            }

            // Calcul des flux
            var fReimbursedAmountTTC = fCreditDurationInMonths * _nMonthlyRepayment ; //montant total à rembourser à la banque [€ TTC]
            var fReimbursedInterestsTTC = fReimbursedAmountTTC - fAmountBorrowedTTC ; //montant des intérêts seuls [€ TTC]
            var fRawProfitOverPeriodTTC = fIncomeOverPeriodTTC + fBillSavingsOverPeriodTTC; //recettes totales pendant la période d'observation (vente + économies) [€ TTC]
            var fSpendingsOverPeriod = fOpexOverPeriodTTC + fReimbursedAmountTTC; //dépenses totales pendant la période d'observation (maintenance + entretien + remboursement du crédit) [€ TTC]
            var fNetIncomingOverPeriod = fRawProfitOverPeriodTTC - fSpendingsOverPeriod; //revenus nets totaux pendant la période d'observation (recettes moins dépenses) [€ TTC]
            var fNetProfitOverPeriod = fNetIncomingOverPeriod - fOwnFundsTTC; //bénéfice net à la fin de la période d'observation (revenus nets moins investissement initial) [€ TTC]
            var fROI = (fNetProfitOverPeriod <=0) ? Number.POSITIVE_INFINITY : fOwnFundsTTC / fNetIncomingOverPeriod * fObservationYearDuration; //temps de retour [années]
            var rateOfReturn = Math.pow(Math.max(1, (fRawProfitOverPeriodTTC - fOpexOverPeriodTTC - fReimbursedInterestsTTC) / fNetInstallationCostWithConnectionTTC), 1 / fObservationYearDuration) - 1; // [-] taux de rentabilité d'un investissement ayant la même VAN que le projet solaire

            return {
                rawInstallationCost:                 fRawInstallationCostWithConnectionTTC,
                installationCost:                    fNetInstallationCostWithConnectionTTC,
                electricityConnectionCostOverPeriod: fNetConnectionPriceTTC,
                electricityCostWoConnection:         fRawInstallationCostWoConnectionTTC,
                borrowedAmount:                      fAmountBorrowedTTC,
                borrowedPercentage:                  borrowedAmountPercentage,
                creditCost:                          fReimbursedInterestsTTC,
                creditAmount:                        fReimbursedAmountTTC,
                creditPeriod:                        fCreditDurationInYears,
                ownFunds:                            fOwnFundsTTC,
                spendingsOverPeriod:                 fSpendingsOverPeriod,
                billSavingsOverPeriod:               fBillSavingsOverPeriodTTC,
                incomingOverPeriod:                  fIncomeOverPeriodTTC,
                maintenanceCostOverPeriod:           fOpexOverPeriodTTC,
                rawProfitOverPeriod:                 Math.max(fRawProfitOverPeriodTTC, 0),
                netProfitOverPeriod:                 Math.max(fNetProfitOverPeriod, 0), // in euros
                totalSubventionOverPeriod:           fSubventionOverPeriodTTC,
                returnOnInvestment:                  fROI,
                observationPeriod:                   fObservationYearDuration,
                rateOfReturn:                        rateOfReturn
            };
        } else {
            return {
                rawInstallationCost:                 Infinity,
                installationCost:                    Infinity,
                electricityConnectionCostOverPeriod: Infinity,
                electricityCostWoConnection:         Infinity,
                borrowedAmount:                      0,
                borrowedPercentage:                  100,
                creditCost:                          Infinity,
                creditAmount:                        Infinity,
                creditPeriod:                        Infinity,
                ownFunds:                            0,
                spendingsOverPeriod:                 Infinity,
                billSavingsOverPeriod:               0,
                incomingOverPeriod:                  0,
                maintenanceCostOverPeriod:           Infinity,
                rawProfitOverPeriod:                 0,
                netProfitOverPeriod:                 0, // in euros
                totalSubventionOverPeriod:           0,
                returnOnInvestment:                  Infinity,
                observationPeriod:                   Infinity,
                rateOfReturn:                        0
            };
        }
    };

    /**
     * Calcul de la valeur actualisee d'une grandeur
     */
    module.PvPanelInstallation.prototype.getDiscountedValue = function(val, actualisationRate, inflation, nYears) {
        var factor = (1 + inflation) / (1 + actualisationRate);
        if (Math.abs(factor) > 2) {
            throw new Error("The actualisation rate is too high, please specify a lower value in the parameter file");
        } else if (Math.abs(factor - 1) < 1e-10) {
            return val * nYears;
        } else {
            return val * factor * (Math.pow(factor, nYears) - 1) / (factor - 1);
        }
    };

    /**
     * Calcul du profil de conso horaire
     * 
     * #2311
     * 
     * @param {Number} annnualConso consomationAnnuel Electric
     */
    module.PvPanelInstallation.prototype._getConsoProfile = function(userInfos, baseConsumption, heatingConsumption) {
        var oProfile = this._params.profiles[userInfos.userCategory];
        if (oProfile === undefined) {
            throw new Error("Cannot load consumption profile for user category '" + userInfos.userCategory + "': unknown user category");
        } else if (!oProfile.hasOwnProperty('baseConsumption')) {
            throw new Error("Cannot load consumption profile for user category '" + userInfos.userCategory + "': unknown base profile");
        } else if (userInfos.hasElectricalHeating && !oProfile.hasOwnProperty('heatingConsumption')) {
            throw new Error("Cannot load consumption profile for user category '" + userInfos.userCategory + "': unknown heating profile");
        }

        var profileA = [];
        for (var iHour = 0; iHour < oProfile.baseConsumption.values.length; ++iHour){
            var consumption = oProfile.baseConsumption.values[iHour] / oProfile.baseConsumption.somme * baseConsumption;
            if (userInfos.hasElectricalHeating) {
                consumption += oProfile.heatingConsumption.values[iHour] / oProfile.heatingConsumption.somme * heatingConsumption;
            }
            profileA.push(consumption);
        }

        return profileA;
    };

    /**
     * Calcul de l'energie autoconsommée
     * 
     * #2311
     * @param {Array} prodProfile production horraire
     * @param {Array} comsumptionProfile consomation horraire
     * 
     * @return consomation annuel autoconsomé
     */
    module.PvPanelInstallation.prototype._computeAutoConso=function(prodProfile,comsumptionProfile){
        var annualAutoConso=0;
        for(var iHour=0;iHour<8760;iHour++){
            var prodValue = prodProfile[iHour],
                consumptionValue = comsumptionProfile[iHour];
            if (!isNaN(prodValue) && !isNaN(consumptionValue)) {
                annualAutoConso += Math.min(prodValue, consumptionValue);
            }
        }
        return annualAutoConso;
    };

    /**
     * Calcul du profil de conso horaire
     * 
     * #2311
     * 
     * @param {Number} annnualProd consomationAnnuel Electric
     */
    module.PvPanelInstallation.prototype._getProdProfile = function(annnualProd) {
        var oProfile=this._params.profiles.prod;
        var meanPower = annnualProd/oProfile.somme;
        var profileA=[];
        for(var iHour=0;iHour<oProfile.values.length;iHour++){
            profileA.push(oProfile.values[iHour]*meanPower);
        }
        return profileA;
    };
    
    
    /**
     * recuperation du bon parametre eco.
     * 
     * #2311
     * 
     * @param {String} sParam nom su parametre a recuperer
     */
    module.PvPanelInstallation.prototype._getEcoParam =function (sParam, fPeakPower, oParam){

        if(typeof oParam =="undefined" || oParam==null ){
            if(this._params["economics_pv"].hasOwnProperty(sParam)){
                oParam=this._params["economics_pv"][sParam];
            }else if(this._params["economics_global"].hasOwnProperty(sParam)){
                oParam=this._params["economics_global"][sParam];
            }else{
                console.warn("missing parameter : "+sParam);
                return null
            }
        }
            
        if(typeof oParam=="object"){
            if(Array.isArray(oParam)){
                if(oParam[0]&&oParam[0].hasOwnProperty("maxPower")){
                    for(var iNumPower=0;iNumPower<oParam.length;iNumPower++){
                        var oData=oParam[iNumPower];
                        if(fPeakPower < oData.maxPower || oData.maxPower == null){
                            return oData.value;
                            break;
                        }
                    }
                } else if (oParam[0]&&oParam[0].hasOwnProperty("power")) {
                    var powers = [];
                    var values = [];
                    oParam.forEach(function(point) {
                        powers.push(point.power);
                        values.push(point.value);
                    });
                    return am.utils.interpolateLinearly(fPeakPower, powers, values);
                } else {
                    return oParam;
                }
            }else{
                if(oParam.hasOwnProperty(this._sUserCategory)){
                    return this._getEcoParam(null, fPeakPower, oParam[this._sUserCategory])
                }else if(oParam.hasOwnProperty(this._sMode)){
                    return this._getEcoParam(null, fPeakPower, oParam[this._sMode]);
                }else{
                    return oParam
                }
            }
        }else{
            return oParam;
        }

    };

    /**
     * Returns the properties of the PV panel installation
     *
     * @return {Objet} - Installation properties. This object has the following keys:
     *      - annualElectricityProduction: [kWh] Annual electricity production
     *      - electricityProductionEquivalent: [-] Number of persons whose annual electricity consumption is covered by 
     *          the production
     *      - pvPanelInstallationCost: [euros] Cost of the installation of the PV panels
     *      - electricitySaleAnnualIncome: [euros] Annual income generated by the sale of electricity
     *      - electricityAutoconsumptionRate: [0-1] Fraction of the consummed electricity which is generated by the PV panels
     *      - annualElectricityBillSavings: [euros] Savings on the annual electricity bill due to the autoconsumption of
     *          electricity
     */
    module.PvPanelInstallation.prototype.getProperties = function() {
        return this._properties;
    };

    /**
     * Returns the specified property of the PV panel installation
     *
     * @param {String} - Name of the property to be returned
     * @param {Number|String} - Value of the requested property
     */
    module.PvPanelInstallation.prototype.getProperty = function(property) {
        return this._properties[property];
    };

    return module;

})(am || {});


//=============================================
// AM.UTILS NAMESPACE
//=============================================
am.utils = (function(module) {

    // Use strict mode
    'use strict';

    /**
     * Returns a new instance of class am.PvPanelInstallation
     *
     * @param {am.ModelBaseClass} roofModel - Reference to the SolarPotentialModel instance to be used in order to 
     *      compute the solar potential of the PV panel installation.
     * @param {Object} params - Object configuration (see constructor of class am.PvPanelInstallation for more information)
     * @return {am.PvPanelInstallation} - New instance of class am.PvPanelInstallation 
     */
    module.pvPanelInstallation = function(roofModel, params) {
        var model = new am.PvPanelInstallation();
        model.init(roofModel, params);
        return model;
    };

    return module;

})(am.utils || {});